import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { AlertTriangle, Shield, Bell, TrendingDown, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const ALERT_COLORS = {
  GREEN: "bg-green-500/10 border-green-500/30 text-green-400",
  YELLOW: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400",
  ORANGE: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  RED: "bg-red-500/10 border-red-500/30 text-red-400",
  CRITICAL: "bg-red-600/20 border-red-600/50 text-red-500 animate-pulse",
};

const ALERT_ICONS = {
  GREEN: Shield,
  YELLOW: Bell,
  ORANGE: AlertTriangle,
  RED: TrendingDown,
  CRITICAL: AlertTriangle,
};

const ALERT_EMOJI = {
  GREEN: "🟢",
  YELLOW: "🟡",
  ORANGE: "🟠",
  RED: "🔴",
  CRITICAL: "🚨",
};

export default function CrashAlertBanner({ minimal = false }) {
  const [alertData, setAlertData] = useState({
    level: "GREEN",
    score: 0,
    recommendation: "🟢 Loading market status...",
    alerts: []
  });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchAlert = useCallback(async () => {
    try {
      const response = await api.get("/market-alert/quick");
      setAlertData(response.data);
    } catch (error) {
      console.error("Error fetching crash alert:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Delay initial fetch to not block page load
    const timer = setTimeout(fetchAlert, 1000);
    // Refresh every 5 minutes (reduced from 3)
    const interval = setInterval(fetchAlert, 300000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [fetchAlert]);

  // Auto-expand for RED/CRITICAL
  useEffect(() => {
    if (alertData?.level === "RED" || alertData?.level === "CRITICAL") {
      setExpanded(true);
      setDismissed(false);
    }
  }, [alertData?.level]);

  // Don't block rendering - show immediately with default data
  if (dismissed && alertData.level === "GREEN") return null;

  const level = alertData.level || "GREEN";
  const AlertIcon = ALERT_ICONS[level];
  const colorClass = ALERT_COLORS[level];
  const emoji = ALERT_EMOJI[level];

  // Minimal version for sidebar/header
  if (minimal) {
    return (
      <div 
        className={`px-3 py-2 rounded-lg border cursor-pointer transition-all hover:scale-105 ${colorClass}`}
        onClick={() => setExpanded(!expanded)}
        data-testid="crash-alert-mini"
      >
        <div className="flex items-center gap-2">
          <AlertIcon className="w-4 h-4" />
          <span className="text-xs font-bold">{level}</span>
          <span className="text-xs">{alertData.score}/100</span>
        </div>
      </div>
    );
  }

  // Full banner
  return (
    <div 
      className={`rounded-lg border p-4 mb-4 ${colorClass}`}
      data-testid="crash-alert-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${level === "CRITICAL" ? "bg-red-600/30" : "bg-current/10"}`}>
            <AlertIcon className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg">{emoji}</span>
              <span className="font-bold text-lg">
                {level === "GREEN" ? "Markets Stable" : 
                 level === "YELLOW" ? "Caution Advised" :
                 level === "ORANGE" ? "Warning - Stay Alert" :
                 level === "RED" ? "DANGER - High Risk" :
                 "🚨 CRITICAL - Action Required!"}
              </span>
              <span className="text-sm opacity-70">
                Risk Score: {alertData.score}/100
              </span>
            </div>
            {!expanded && alertData.alerts?.length > 0 && (
              <p className="text-sm opacity-70 mt-1">
                {alertData.alerts[0]}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAlert}
            className="opacity-70 hover:opacity-100"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
          {level === "GREEN" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDismissed(true)}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-current/20 pt-4">
          {/* Recommendation */}
          <div className="p-3 rounded bg-black/20">
            <p className="text-sm font-semibold mb-1">💡 Recommendation:</p>
            <p className="text-sm">{alertData.recommendation}</p>
          </div>

          {/* Active Alerts */}
          {alertData.alerts?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">🔔 Active Signals:</p>
              <div className="space-y-1">
                {alertData.alerts.map((alert, idx) => (
                  <p key={idx} className="text-sm opacity-90">• {alert}</p>
                ))}
              </div>
            </div>
          )}

          {/* Key Indicators */}
          {alertData.indicators && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              {alertData.indicators.vix && (
                <div className="p-2 rounded bg-black/20 text-center">
                  <p className="text-xs opacity-70">VIX</p>
                  <p className="font-bold">{alertData.indicators.vix.current?.toFixed(1)}</p>
                  <p className={`text-xs ${alertData.indicators.vix.daily_change > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {alertData.indicators.vix.daily_change > 0 ? '+' : ''}{alertData.indicators.vix.daily_change}%
                  </p>
                </div>
              )}
              {alertData.indicators.spy && (
                <div className="p-2 rounded bg-black/20 text-center">
                  <p className="text-xs opacity-70">S&P 500</p>
                  <p className="font-bold">${alertData.indicators.spy.current?.toLocaleString()}</p>
                  <p className={`text-xs ${alertData.indicators.spy.daily_change < 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {alertData.indicators.spy.daily_change > 0 ? '+' : ''}{alertData.indicators.spy.daily_change}%
                  </p>
                </div>
              )}
              {alertData.indicators.uvxy && (
                <div className="p-2 rounded bg-black/20 text-center">
                  <p className="text-xs opacity-70">UVXY (Fear)</p>
                  <p className="font-bold">${alertData.indicators.uvxy.current?.toFixed(2)}</p>
                  <p className={`text-xs ${alertData.indicators.uvxy.five_day_change > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {alertData.indicators.uvxy.five_day_change > 0 ? '+' : ''}{alertData.indicators.uvxy.five_day_change}% (5d)
                  </p>
                </div>
              )}
              {alertData.indicators.sectors && (
                <div className="p-2 rounded bg-black/20 text-center">
                  <p className="text-xs opacity-70">Sector Divergence</p>
                  <p className="font-bold">{alertData.indicators.sectors.divergence}%</p>
                  <p className={`text-xs ${alertData.indicators.sectors.danger_signal ? 'text-red-400' : 'text-green-400'}`}>
                    {alertData.indicators.sectors.danger_signal ? '⚠️ Flight to Safety' : 'Normal'}
                  </p>
                </div>
              )}
            </div>
          )}

          <p className="text-xs opacity-50 mt-2">
            Last updated: {alertData.last_update ? new Date(alertData.last_update).toLocaleTimeString() : 'N/A'}
            {' '}• Auto-refreshes every 3 minutes
          </p>
        </div>
      )}
    </div>
  );
}
